import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { CaseSlideShell } from './CaseSlideShell';
import { PdfImage } from '../PdfImage';
import { boehringerImages } from '../../data/boehringerSnak';
const STEPS = [{
  label: 'See',
  line: 'A striking patient fact',
  crop: boehringerImages.statScreen,
  alt: 'Snak screen: 332,507 people in Denmark diagnosed with type 2 diabetes'
}, {
  label: 'Think',
  line: 'A clinical question',
  crop: boehringerImages.quizScreen,
  alt: 'Snak quiz: what share of T2D and CVD patients receive neither SGLT2 nor GLP-1?'
}, {
  label: 'Answer',
  line: 'HCPs commit to a view',
  crop: boehringerImages.quizResults,
  alt: 'Answer distribution for the quiz question'
}, {
  label: 'Learn',
  line: 'Instant clinical feedback',
  crop: boehringerImages.correctScreen,
  alt: 'Feedback screen: Correct! 47% are not on organ-protective treatment'
}, {
  label: 'Act',
  line: 'Choose a relevant resource',
  crop: boehringerImages.ctaScreen,
  alt: 'Snak menu: Want to know more?'
}] as const;
const MEASURES = [{
  q: 'Did they click?',
  strong: false
}, {
  q: 'How long did they stay?',
  strong: true
}, {
  q: 'What did they answer?',
  strong: true
}, {
  q: 'What did they want next?',
  strong: true
}] as const;
export function CaseConversation() {
  return <CaseSlideShell step="03" eyebrow="From communication to conversation" headline={<>
          Don’t just send information.{' '}
          <span className="text-[var(--brand)]" data-id="element-76" data-mp="urrcpfk559f6">Invite a response.</span>
        </>} data-id="element-75" data-mp="31mo14pcm085">
      <ol className="flex min-h-0 flex-1 items-stretch gap-[clamp(0.25rem,0.6vw,0.6rem)]" data-id="element-77" data-mp="qq5tlch0jywj">
        {STEPS.map((step, index) => <React.Fragment key={step.label} data-id="element-78" data-mp="lrvy10rdmwae" data-mpi={index}>
            <motion.li initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15 + index * 0.1,
          duration: 0.45
        }} className="flex min-h-0 min-w-0 flex-1 flex-col items-center" data-id="element-79" data-mp="lt73r64due45">
              <span className="flex-shrink-0 text-[clamp(1.4rem,2.6vw,2.5rem)] font-extrabold tracking-[-0.04em] text-[var(--brand)]" data-id="element-80" data-mp="37z0ioikpepsi">
                {step.label}
              </span>
              <div className="mt-[clamp(0.4rem,0.8vw,0.7rem)] flex min-h-0 w-full flex-1 items-center justify-center" data-id="element-81" data-mp="2ywrba1kqfcjc">
                <PdfImage crop={step.crop} alt={step.alt} className="max-h-full w-full rounded-xl bg-white object-contain shadow-[0_10px_28px_rgba(23,32,51,0.14)] ring-1 ring-black/10" data-id="element-82" data-mp="32xtcgny7lyny" />
              </div>
              <span className="mt-[clamp(0.4rem,0.8vw,0.7rem)] flex-shrink-0 text-center text-[clamp(0.7rem,1vw,0.95rem)] font-semibold leading-snug text-[var(--muted)]" data-id="element-83" data-mp="2fauvlp66gopi">
                {step.line}
              </span>
            </motion.li>
            {index < STEPS.length - 1 && <li className="flex flex-shrink-0 items-center" aria-hidden="true" data-id="element-84" data-mp="2b8htdaqn38xh">
                <ArrowRightIcon className="h-4 w-4 text-[var(--brand)]" data-id="element-85" data-mp="1rm6k59frpxn1" />
              </li>}
          </React.Fragment>)}
      </ol>

      <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.75,
      duration: 0.5
    }} className="mt-[clamp(0.9rem,1.8vw,1.5rem)] flex flex-shrink-0 flex-wrap items-center gap-x-[clamp(1rem,2vw,2rem)] gap-y-2 rounded-2xl bg-[var(--surface)] px-[clamp(1rem,1.8vw,1.6rem)] py-[clamp(0.7rem,1.3vw,1.1rem)]" data-id="element-86" data-mp="1mp6aypnmgerp">
        <p className="text-[clamp(0.85rem,1.2vw,1.15rem)] font-extrabold tracking-[-0.02em] text-[var(--fg)]" data-id="element-87" data-mp="25lejq3fuf7rr">
          Measure how HCPs engaged and what they thought — not just whether they clicked.
        </p>
        <ul className="flex flex-wrap gap-2" data-id="element-88" data-mp="2fcsp2ruh71st">
          {MEASURES.map(m => <li key={m.q} className={`rounded-full px-3 py-1 text-[clamp(0.65rem,0.88vw,0.82rem)] font-semibold ${m.strong ? 'bg-[var(--brand)] text-white' : 'bg-white text-[var(--muted)] line-through decoration-[var(--muted)]/60'}`} data-id="element-89" data-mp="2tcjxmd283pzf">
              {m.q}
            </li>)}
        </ul>
      </motion.div>
    </CaseSlideShell>;
}